import React, { useState } from 'react';
import bodyFront from '../assets/images/body-front.png';
import bodyBack from '../assets/images/body-back.png';

function BodyChart({ markers = [], onChange }) {
  const [activeView, setActiveView] = useState('front');
  const [selectedMarker, setSelectedMarker] = useState(null);

  const handleClick = (e, view) => {
    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    const newMarker = {
      id: Date.now(),
      x: Math.round(x * 10) / 10,
      y: Math.round(y * 10) / 10,
      view,
      label: ''
    };
    
    onChange([...markers, newMarker]);
    setSelectedMarker(newMarker.id);
  };

  const updateMarkerLabel = (id, label) => {
    onChange(markers.map(m => m.id === id ? { ...m, label } : m));
  };

  const removeMarker = (id) => {
    onChange(markers.filter(m => m.id !== id));
    setSelectedMarker(null);
  };

  const renderBodyImage = (view) => (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '320px',
        cursor: 'crosshair',
        background: '#fefefe',
        borderRadius: '8px',
        border: activeView === view ? '2px solid #10b981' : '1px solid #e5e7eb',
        overflow: 'hidden'
      }}
      onClick={(e) => handleClick(e, view)}
    >
      <img
        src={view === 'front' ? bodyFront : bodyBack}
        alt={`Body ${view} view`}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          pointerEvents: 'none'
        }}
      />
      {markers
        .filter(m => m.view === view)
        .map(marker => (
          <div
            key={marker.id}
            onClick={(e) => { e.stopPropagation(); setSelectedMarker(marker.id); }}
            style={{
              position: 'absolute',
              left: `${marker.x}%`,
              top: `${marker.y}%`,
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              zIndex: 10,
              cursor: 'pointer'
            }}
          >
            <div style={{
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              background: selectedMarker === marker.id ? '#ef4444' : '#10b981',
              border: '2px solid white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              flexShrink: 0
            }} />
            {marker.label && (
              <span style={{
                background: 'rgba(255,255,255,0.9)',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: 500,
                color: '#374151',
                whiteSpace: 'nowrap',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                {marker.label}
              </span>
            )}
          </div>
        ))}
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <button
          type="button"
          onClick={() => setActiveView('front')}
          style={{
            padding: '0.25rem 0.75rem',
            background: activeView === 'front' ? '#10b981' : '#f3f4f6',
            color: activeView === 'front' ? 'white' : '#374151',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.8rem'
          }}
        >
          Front
        </button>
        <button
          type="button"
          onClick={() => setActiveView('back')}
          style={{
            padding: '0.25rem 0.75rem',
            background: activeView === 'back' ? '#10b981' : '#f3f4f6',
            color: activeView === 'back' ? 'white' : '#374151',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.8rem'
          }}
        >
          Back
        </button>
        <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: 'auto', alignSelf: 'center' }}>
          Click to add marker
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.25rem', textAlign: 'center' }}>FRONT</div>
          {renderBodyImage('front')}
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.25rem', textAlign: 'center' }}>BACK</div>
          {renderBodyImage('back')}
        </div>
      </div>

      {markers.length > 0 && (
        <div style={{ marginTop: '0.75rem' }}>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>
            Markers ({markers.length}):
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {markers.map((marker, idx) => (
              <div
                key={marker.id}
                onClick={() => setSelectedMarker(marker.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  padding: '0.25rem 0.5rem',
                  background: selectedMarker === marker.id ? '#dcfce7' : '#f3f4f6',
                  borderRadius: '4px',
                  fontSize: '0.8rem',
                  cursor: 'pointer'
                }}
              >
                <span style={{ 
                  width: '16px', 
                  height: '16px', 
                  background: '#10b981', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '0.65rem'
                }}>
                  {idx + 1}
                </span>
                <input
                  type="text"
                  placeholder="Label..."
                  value={marker.label}
                  onChange={(e) => updateMarkerLabel(marker.id, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    width: '80px',
                    fontSize: '0.8rem',
                    outline: 'none'
                  }}
                />
                <span style={{ fontSize: '0.65rem', color: '#9ca3af' }}>
                  ({marker.view})
                </span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeMarker(marker.id); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ef4444',
                    cursor: 'pointer',
                    padding: '0',
                    fontSize: '1rem',
                    lineHeight: 1
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default BodyChart;
